// Plan card — the third widget in the dashboard band.
//
// Free: always shows the upgrade upsell (this is a standing CTA, not a
// near-limit nudge), and surfaces a small note when any code is past or
// close to the per-code 50-scan free limit. Pro/staff: a status card with
// a Manage-billing shortcut. The per-code scan paywall still gates editing and
// detailed analytics regardless of this card (see Entitlements).
export const APPROACH_FRACTION = 0.8

// Where a code stands against its free scans: "past" (over the threshold, so
// editing is locked, the server's rule in Entitlements#code_editable?),
// "near" (at APPROACH_FRACTION of it or more), or null. The threshold is
// QrCode::SCAN_PAYWALL_THRESHOLD, sent on every row. A row without one (a
// code the builder just made) has no scans to count yet.
export function scanLimitState(code) {
  const limit = code.scan_paywall_threshold
  if (!limit) return null
  const scans = code.scan_count || 0
  if (scans > limit) return "past"
  if (scans >= Math.floor(limit * APPROACH_FRACTION)) return "near"
  return null
}

const quoted = (code) => `“${code.display_label}”`

function ScanCount({ code }) {
  return <span className="font-mono">{(code.scan_count || 0).toLocaleString()} of {code.scan_paywall_threshold}</span>
}

// The words for codes past their free scans and codes close to them (#327).
// The list's notices (YourCodesCard) and this card's note both use these, so
// the two never disagree. One or two codes are named as the list names them;
// more are counted.
export function PastLimitText({ codes }) {
  const limit = codes[0].scan_paywall_threshold
  if (codes.length === 1) return <>{quoted(codes[0])} has passed its {limit} free scans, so editing is locked. It keeps redirecting.</>
  const who = codes.length === 2 ? `${quoted(codes[0])} and ${quoted(codes[1])}` : `${codes.length} codes`
  return <>{who} have passed their {limit} free scans, so editing is locked. They keep redirecting.</>
}

export function NearLimitText({ codes }) {
  const limit = codes[0].scan_paywall_threshold
  return (
    <>
      {codes.length === 1 ? (
        <>{quoted(codes[0])} is close to its {limit} free scans: <ScanCount code={codes[0]} />.</>
      ) : codes.length === 2 ? (
        <>{quoted(codes[0])} (<ScanCount code={codes[0]} />) and {quoted(codes[1])} (<ScanCount code={codes[1]} />) are close to their {limit} free scans.</>
      ) : (
        <>{codes.length} codes are close to their {limit} free scans.</>
      )}{" "}
      Editing locks after scan {limit}.
    </>
  )
}

function SparkleBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-plasma/15 dark:bg-plasma/25 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-plasma dark:text-plasma-light mb-3">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
      </svg>
      Pro
    </span>
  )
}

export default function PlanCard({ codes, isPaid, busy, onUpgrade, onManageBilling }) {
  if (isPaid) {
    return (
      <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg p-5">
        <SparkleBadge />
        <div className="font-display font-bold text-lg text-ink dark:text-cloud mb-1">You're steering every code</div>
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mb-4">
          Full edits + analytics on every code, no wordmark. Thanks for going Pro.
        </p>
        <button
          type="button"
          onClick={onManageBilling}
          disabled={busy}
          className="w-full border border-[color:var(--color-border)] dark:border-storm text-ink dark:text-cloud rounded-lg px-3 py-2 text-sm font-medium hover:border-voltage hover:text-voltage-dark dark:hover:text-voltage-light transition disabled:opacity-50"
        >
          {busy ? "Opening…" : "Manage billing"}
        </button>
      </div>
    )
  }

  // Free — standing upsell, with a note when a code is past its free scans
  // or close to them, in the same words as the list's notices (#327).
  const past = (codes || []).filter((c) => scanLimitState(c) === "past")
  const near = (codes || []).filter((c) => scanLimitState(c) === "near")

  return (
    <div className="bg-plasma-pale dark:bg-plasma/10 border border-plasma/30 dark:border-plasma/40 rounded-lg p-5">
      <SparkleBadge />
      <div className="font-display font-bold text-lg text-ink dark:text-cloud mb-1">Keep steering every code</div>
      <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mb-4">
        Edit &amp; full analytics on every code, forever. No wordmark. Module &amp; eye shapes.{" "}
        <strong className="text-ink dark:text-cloud font-semibold">$10/mo.</strong>
      </p>

      {(past.length > 0 || near.length > 0) && (
        <div className="mb-4 rounded-md bg-white/60 dark:bg-coal/40 border border-plasma/20 dark:border-plasma/30 px-3 py-2 text-xs text-ink dark:text-cloud flex flex-col gap-1 break-words">
          {past.length > 0 && <p className="font-semibold"><PastLimitText codes={past} /></p>}
          {near.length > 0 && <p><NearLimitText codes={near} /></p>}
        </div>
      )}

      <button
        type="button"
        onClick={onUpgrade}
        disabled={busy}
        className="w-full bg-plasma hover:bg-plasma-dark text-white rounded-lg px-3 py-2.5 text-sm font-semibold transition disabled:opacity-50"
      >
        {busy ? "Starting…" : "Upgrade to Pro"}
      </button>
    </div>
  )
}
